// Another device, case B (36-desyr-id.md, Sign in on this device and Behaviour 7.2; 94, desyr ID rule 7;
// v1.58.46, v1.58.47): the card that asks for the emailed link, and the link's page `/id/device/`, which
// shows the desyr ID's email from the look call and claims nothing until Continue. Only with
// `desyrId.deviceLink` on. The calls are JSON without cookies; the token travels only in their bodies.
import { useEffect, useState, type FormEvent } from 'react';
import type { Config, DesyrIdConfig } from '../core/config';
import { describe } from '../core/errors';
import { currentLanguage, t } from '../i18n';
import { lastEmail } from '../signin/company';
import { ErrorLine, Field, Frame, Heading, MainButton, Mark } from '../signin/parts';

/** The link's token, taken out of the address and the history before anything is drawn (Behaviour 7.2.2). */
const token = (() => {
  if (location.pathname !== '/id/device/') return '';
  const found = location.hash.slice(1);
  if (found) history.replaceState(history.state, '', '/id/device/');
  return found;
})();

/** A signed-out call under `desyrId.admin`; its error code, or `desyr.id.failed` when there is none. */
async function deviceCall<T>(id: DesyrIdConfig, path: string, body: object): Promise<T> {
  const answer = await fetch(`${new URL(id.admin, location.origin).href.replace(/\/+$/, '')}${path}`, {
    method: 'POST',
    credentials: 'omit',
    cache: 'no-store',
    referrerPolicy: 'no-referrer',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  }).catch(() => undefined);
  if (answer?.ok) return answer.json().catch(() => ({}) as T);
  const code = answer && (await answer.json().catch(() => ({}))).code;
  throw Object.assign(new Error(code ?? 'desyr.id.failed'), { status: answer?.status, code: code ?? (answer ? 'desyr.id.failed' : 'desyr.id.unavailable') });
}

/**
 * Plain words for a refused call (36, Sign in on this device, Errors); never the service's own. The
 * invalid requests (403 `desyr.request.origin`, 400 `desyr.request.not_json` and `desyr.request.bad`;
 * 94, v1.58.56) are 02's invalid kind, worded as an unknown failure.
 */
const wordsFor = (e: unknown) => {
  const code = (e as { code?: string }).code;
  if (code?.startsWith('desyr.request.')) return describe(e, 'general').text;
  return code === 'desyr.id.link_used' ? t('id.linkUsed') : code === 'desyr.id.too_many' ? t('id.tooManyLinks') : code === 'desyr.id.unavailable' ? t('id.unavailable') : t('id.failed');
};

/** "Sign in on this device" (`data-desyr-sign-in-step` "device-link"): the email, Email me a link, then "Check your email". */
export function SignInHere({ config, id, onBack }: { config: Config; id: DesyrIdConfig; onBack: () => void }) {
  const [email, setEmail] = useState(lastEmail);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<string>();
  const [again, setAgain] = useState(false);
  useEffect(() => {
    if (!sent) return;
    setAgain(false);
    const timer = setTimeout(() => setAgain(true), 60_000); // Send again after 60 s
    return () => clearTimeout(timer);
  }, [sent]);
  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const address = email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) return setError(t('id.badEmail'));
    setBusy(true);
    setError(undefined);
    try {
      await deviceCall(id, '/device-link', { email: address, language: currentLanguage() === 'de' ? 'de' : 'en' });
      setSent(address);
    } catch (problem) {
      setError(wordsFor(problem));
    } finally {
      setBusy(false);
    }
  };
  if (sent)
    return (
      <Frame config={config} step="device-link">
        <Mark kind="sent" />
        <Heading sub={t('id.linkOnItsWay', { email: sent })}>{t('id.checkEmail')}</Heading>
        <div className="signin-form">
          <ErrorLine>{error}</ErrorLine>
          <button type="button" className="si-link si-under" onClick={onBack}>{t('id.backToSignIn')}</button>
          {again && <button type="button" className="si-link si-under" onClick={() => void send()}>{t('id.sendAgain')}</button>}
        </div>
      </Frame>
    );
  return (
    <Frame config={config} waiting={busy} step="device-link">
      <Heading sub={t('id.hereLine')}>{t('id.signInHere')}</Heading>
      <p className="si-quiet-line">{t('id.passwordNoNeed')}</p>
      <form className="signin-form" onSubmit={(e) => void send(e)} noValidate>
        <Field label={t('id.email')} type="email" inputMode="email" autoComplete="email" spellCheck={false} autoCapitalize="none" value={email} invalid={!!error} error={error} onChange={(e) => setEmail(e.target.value)} />
        <MainButton waiting={busy}>{busy ? t('id.sending') : t('id.emailMeLink')}</MainButton>
        <button type="button" className="si-link si-under" onClick={onBack}>{t('common.back')}</button>
        <p className="si-quiet-line">{t('id.phoneAtHand')}</p>
      </form>
    </Frame>
  );
}

/**
 * The link's page (`/id/device/`, `data-desyr-sign-in-step` "device"): opening it claims nothing (the
 * look call says whose desyr ID it is); Continue sends the redemption; Cancel shows the sign-in card.
 */
export function DevicePage({ config, onCancel, onNewLink }: { config: Config; onCancel: () => void; onNewLink: () => void }) {
  const id = config.desyrId?.deviceLink ? config.desyrId : undefined;
  const [email, setEmail] = useState<string>();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<{ words: string; used?: boolean }>();
  useEffect(() => {
    if (!id || !token) return setError({ words: t('id.linkUsed'), used: true });
    deviceCall<{ email: string }>(id, '/device-link/look', { token }).then(
      (answer) => setEmail(answer.email),
      (e) => setError({ words: wordsFor(e), used: (e as { code?: string }).code === 'desyr.id.link_used' }),
    );
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const redeem = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await deviceCall(id!, '/device-link/redeem', { token });
      setDone(true);
    } catch (problem) {
      // Unreachable: nothing was claimed, Continue stays (36, Errors).
      setError({ words: wordsFor(problem), used: (problem as { code?: string }).code === 'desyr.id.link_used' });
    } finally {
      setBusy(false);
    }
  };
  if (done)
    return (
      <Frame config={config} step="device">
        <Mark kind="sent" />
        <Heading sub={t('id.passkeysGoing')}>{t('id.checkEmailAgain')}</Heading>
      </Frame>
    );
  if (error?.used || (!email && error))
    return (
      <Frame config={config} step="device">
        <Mark kind="error" />
        <Heading>{t('id.setUpDevice')}</Heading>
        <div className="signin-form">
          <ErrorLine>{error.words}</ErrorLine>
          {error.used && id && <button type="button" className="si-link si-under" onClick={onNewLink}>{t('id.askNewLink')}</button>}
          <button type="button" className="si-link si-under" onClick={onCancel}>{t('common.cancel')}</button>
        </div>
      </Frame>
    );
  return (
    <Frame config={config} waiting={!email || busy} step="device">
      <Heading sub={email && t('id.setUpLine', { email })}>{t('id.setUpDevice')}</Heading>
      <p className="si-quiet-line">{t('id.ownersNeedPasskey')}</p>
      <form className="signin-form" onSubmit={(e) => void redeem(e)}>
        <ErrorLine>{error?.words}</ErrorLine>
        <MainButton waiting={busy} disabled={!email}>{busy ? t('id.settingUp') : t('common.continue')}</MainButton>
        <button type="button" className="si-link si-under" onClick={onCancel}>{t('common.cancel')}</button>
      </form>
    </Frame>
  );
}
